import { useContext, useEffect, useState } from 'react';
import { useAppDispatch } from 'store/store';
import { setAssessmentsDetails } from '../assessmentsAuthSlice';
import {
  ASSESSMENTS_NATIVE_BRIDGE_TIMEOUT_MS,
  AssessmentsNativeInboundEventType,
} from '../constants';
import {
  AssessmentsNativeAuthPayload,
  AssessmentsNativeInboundEvent,
  isInNativeAssessmentsWebview,
} from '../utils/nativeBridge';
import { consumeNativeBridgeQueue } from '../utils/nativeBridgeQueue';
import { AssessmentsNativeBridgeContext } from '../contexts/AssessmentsNativeBridgeContext';
import { useAssessmentsTokenRefresh } from './useAssessmentsTokenRefresh';

export type AssessmentsNativeBridgeStatus = 'idle' | 'waiting' | 'received' | 'timeout';

// The single consumer of the mobile app's inbound event queue (window.postAssessmentsNativeEvent).
// Mounted by UnauthenticatedAssessmentsGuard, it stays subscribed for as long as the page is in
// the app's webview and handles every event type:
//
// - SET_AUTH: the initial auth (isUserVerified + accessToken, plus the assessment identifiers), so
//   the guard can skip the manual verify-login screen. Only honoured while waiting for it.
// - UPDATE_TOKEN: the refreshed access token native sends back after REFRESH_TOKEN.
// - ASSESSMENT_CLOSE_REQUESTED: forwarded to the handler the mounted assessment registers via
//   useAssessmentsCloseRequested (via AssessmentsNativeBridgeContext); ignored if none.
//
// Any other/unrecognized event type is ignored rather than assumed to be auth - add a case below
// (and a variant on AssessmentsNativeInboundEvent) to handle one.
//
// Consuming the queue itself is delegated to consumeNativeBridgeQueue, which polls on a plain
// interval outside of React's render cycle - this hook never re-checks the queue on rerender.
//
// - 'idle': not running inside the native webview (no bridge handler present) - nothing to wait for.
// - 'waiting': inside the webview, holding for a SET_AUTH event.
// - 'received': native delivered SET_AUTH; setAssessmentsDetails has been dispatched.
// - 'timeout': no SET_AUTH event arrived within ASSESSMENTS_NATIVE_BRIDGE_TIMEOUT_MS - caller
//   should fall back to the normal verify-login flow.
export const useAssessmentsNativeBridge = (): { status: AssessmentsNativeBridgeStatus } => {
  const dispatch = useAppDispatch();
  const updateAccessToken = useAssessmentsTokenRefresh();
  const closeRequestedHandlerRef = useContext(AssessmentsNativeBridgeContext);
  if (!closeRequestedHandlerRef) {
    throw new Error(
      'useAssessmentsNativeBridge must be used within an AssessmentsNativeBridgeProvider',
    );
  }
  const [isInWebview] = useState(isInNativeAssessmentsWebview);
  const [status, setStatus] = useState<AssessmentsNativeBridgeStatus>(
    isInWebview ? 'waiting' : 'idle',
  );

  useEffect(() => {
    if (!isInWebview) {
      return undefined;
    }

    // Set once SET_AUTH is applied or the wait times out, so a late or repeated SET_AUTH is not
    // acted on after the guard has already moved on.
    let authSettled = false;

    const applyAuth = (payload: AssessmentsNativeAuthPayload) => {
      dispatch(
        setAssessmentsDetails({
          accessToken: payload.accessToken ?? null,
          expiresAt: payload.expiresAt ?? null,
          assessmentsId: payload.assessmentsId ?? null,
          activityId: payload.activityId ?? null,
          clientPatientId: payload.clientPatientId ?? null,
          firstName: payload.firstName ?? null,
          source: payload.source,
        }),
      );
      authSettled = true;
      setStatus('received');
    };

    const handleEvent = (event: AssessmentsNativeInboundEvent) => {
      switch (event.type) {
        case AssessmentsNativeInboundEventType.SET_AUTH:
          if (!authSettled) {
            applyAuth(event.payload);
          }
          break;
        case AssessmentsNativeInboundEventType.UPDATE_TOKEN:
          updateAccessToken(event.payload.accessToken, event.payload.expiresAt);
          break;
        case AssessmentsNativeInboundEventType.ASSESSMENT_CLOSE_REQUESTED:
          closeRequestedHandlerRef.current?.(event.eventId);
          break;
        default:
          // Unhandled event type - ignore it rather than assume it's auth.
          console.warn('Unhandled native assessments bridge event:', event);
      }
    };

    const stopPolling = consumeNativeBridgeQueue(handleEvent);

    // Skip starting the timeout at all if the immediate, synchronous drain above already got auth.
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (!authSettled) {
      timer = setTimeout(() => {
        if (!authSettled) {
          authSettled = true;
          setStatus('timeout');
        }
      }, ASSESSMENTS_NATIVE_BRIDGE_TIMEOUT_MS);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      stopPolling();
    };
  }, [isInWebview, dispatch, updateAccessToken, closeRequestedHandlerRef]);

  return { status };
};
